import React from 'react';

import {SeasonalMetricSet} from '../../lib/api/eval';

const HEIGHT = 240;
const MARGIN = {top: 24, right: 24, bottom: 40, left: 48};

const HORIZON_ORDER = ['30d', '45d', '60d', '90d', '180d', '365d'];
const HORIZON_LABELS: Record<string, string> = {
  '30d': '30d',
  '45d': '45d',
  '60d': '60d',
  '90d': '90d',
  '180d': '180d',
  '365d': '365d',
};

const LINE_COLOR = '#3b82f6'; // blue-500
const DOT_COLOR = '#2563eb'; // blue-600

type MetricKey = 'kge' | 'nrmse' | 'mae' | 'percentBias';

export default function LeadHorizonChart({
  data,
  metric = 'kge',
}: {
  data?: Record<string, SeasonalMetricSet>;
  metric?: MetricKey;
}) {
  const containerRef = React.useRef<HTMLDivElement>(null);
  const [width, setWidth] = React.useState(600);
  const [hoveredIdx, setHoveredIdx] = React.useState<number | null>(null);

  React.useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setWidth(Math.round(entry.contentRect.width));
      }
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  if (!data) {
    return <div className="text-sm text-gray-400 text-center py-8">No horizon data available</div>;
  }

  const points: Array<{
    horizon: string;
    label: string;
    value: number | null;
    count: number;
    idx: number;
  }> = [];
  for (let i = 0; i < HORIZON_ORDER.length; i++) {
    const h = HORIZON_ORDER[i];
    const entry = data[h];
    const raw = entry?.[metric];
    points.push({
      horizon: h,
      label: HORIZON_LABELS[h],
      value: raw != null && Number.isFinite(raw) ? raw : null,
      count: entry?.forecastCount ?? 0,
      idx: i,
    });
  }

  const validPoints = points.filter((p) => p.value !== null);
  if (validPoints.length === 0) {
    return <div className="text-sm text-gray-400 text-center py-8">No horizon data available</div>;
  }

  const validValues = validPoints.map((p) => p.value!);
  const minVal = Math.min(...validValues);
  const maxVal = Math.max(...validValues);
  const range = maxVal - minVal || 1;
  const yMin = minVal - range * 0.15;
  const yMax = maxVal + range * 0.15;

  const graphW = width - MARGIN.left - MARGIN.right;
  const graphH = HEIGHT - MARGIN.top - MARGIN.bottom;

  const yScale = (v: number) => MARGIN.top + graphH - ((v - yMin) / (yMax - yMin)) * graphH;
  const xScale = (idx: number) => MARGIN.left + (idx / (HORIZON_ORDER.length - 1)) * graphW;

  // Build path
  const pathParts: string[] = [];
  for (const p of validPoints) {
    const x = xScale(p.idx);
    const y = yScale(p.value!);
    pathParts.push(`${pathParts.length === 0 ? 'M' : 'L'} ${x} ${y}`);
  }

  return (
    <div ref={containerRef}>
      <svg viewBox={`0 0 ${width} ${HEIGHT}`} width="100%" height={HEIGHT}>
        {/* Grid lines */}
        {[0, 0.25, 0.5, 0.75, 1].map((frac) => {
          const val = yMin + frac * (yMax - yMin);
          const y = yScale(val);
          return (
            <g key={frac}>
              <line
                x1={MARGIN.left}
                x2={width - MARGIN.right}
                y1={y}
                y2={y}
                stroke="#e5e7eb"
                strokeWidth={0.5}
              />
              <text x={MARGIN.left - 4} y={y} textAnchor="end" fontSize={10} fill="#6b7280" dy={3}>
                {val.toFixed(2)}
              </text>
            </g>
          );
        })}

        {/* Line */}
        <path d={pathParts.join(' ')} fill="none" stroke={LINE_COLOR} strokeWidth={2} />

        {/* Dots + labels */}
        {points.map((p) => {
          const x = xScale(p.idx);
          const isHov = hoveredIdx === p.idx;

          return (
            <g
              key={p.horizon}
              onMouseEnter={() => setHoveredIdx(p.idx)}
              onMouseLeave={() => setHoveredIdx(null)}
            >
              {/* Hit area */}
              <rect
                x={x - 20}
                y={MARGIN.top}
                width={40}
                height={graphH}
                fill="transparent"
                style={{cursor: 'default'}}
              />

              {p.value !== null && (
                <>
                  <circle
                    cx={x}
                    cy={yScale(p.value)}
                    r={isHov ? 5 : 3.5}
                    fill="white"
                    stroke={DOT_COLOR}
                    strokeWidth={2}
                  />
                  {isHov && (
                    <text
                      x={x}
                      y={yScale(p.value) - 10}
                      textAnchor="middle"
                      fontSize={10}
                      fill="#374151"
                      fontWeight="bold"
                    >
                      {p.value.toFixed(3)} (n={p.count})
                    </text>
                  )}
                </>
              )}

              {/* X axis label */}
              <text
                x={x}
                y={HEIGHT - MARGIN.bottom + 20}
                textAnchor="middle"
                fontSize={10}
                fill="#374151"
              >
                {p.label}
              </text>
            </g>
          );
        })}

        {/* Axis labels */}
        <text
          x={MARGIN.left + graphW / 2}
          y={HEIGHT - 4}
          textAnchor="middle"
          fontSize={11}
          fill="#6b7280"
        >
          Lead-time horizon
        </text>
        <text
          x={MARGIN.left / 2}
          y={MARGIN.top + graphH / 2}
          textAnchor="middle"
          fontSize={11}
          fill="#6b7280"
          transform={`rotate(-90, ${MARGIN.left / 2}, ${MARGIN.top + graphH / 2})`}
        >
          {metric === 'kge'
            ? 'KGE'
            : metric === 'nrmse'
              ? 'nRMSE'
              : metric === 'mae'
                ? 'MAE'
                : '%Bias'}
        </text>
      </svg>
    </div>
  );
}
